/* eslint-disable no-param-reassign */
import { isEmpty } from 'lodash';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import { GRID_NUMBER_CHECKBOX } from 'common/constants/gridType';
import { SELECTION_A, SELECTION_B } from 'common/constants/valuations';
import {
  PERCENTILE_SELECTION_A_ALIAS,
  PERCENTILE_SELECTION_B_ALIAS,
  SELECTED_DATABASE_VALUE,
  SELECTED_MULTIPLE,
  SELECTION,
  SPECIFIED_LABEL,
  VALUES_OPTIONS,
  WEIGHTED_EV,
  WEIGHTING,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { percentileOptions } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/getRowConfig';
import { APPROACH_GPC_PROPERTY } from 'pages/Valuations/util/constants';
import { getFixedValue, trimUpdateFields } from 'pages/Valuations/util/util';
import {
  EDITABLE_COLUMNS,
  ENTERPRISE_VALUE_ID,
  FINANCIALS_COMPANY_INFO_KEYS,
  GPC_NTM_EBITDA_APPROACH_ALIAS,
  GPC_NTM_REVENUE_APPROACH_ALIAS,
  LTM_EBITDA_ID,
  LTM_REVENUE_ID,
  MULTIPLE_COLUMNS_LETTER,
  MULTIPLE_DISCOUNT_ALIAS,
  NTM_MULTIPLES_IDS,
  REFERENCE_FOR_NTM_EBITDA,
  REFERENCE_FOR_NTM_REVENUE,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/config/constants';
import {
  VALUATIONS_SPECIFIED_ID,
  VALUATIONS_SPECIFIED_LABEL,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { GPTApproachAttributes } from 'pages/ValuationsAllocation/types';
import { updateApproachReference } from 'pages/ValuationsAllocation/util';
import { getArrayValue, getObjectValue, getStringValue } from 'utilities';
import {
  ColumnSelectedMultipleKey,
  ColumnSelection,
  ColumnWeightKey,
  CreateTransactionCompsConfigParams,
  DiscountType,
  PercentileSelection,
  ReverseParsedValuationApproachGPT,
  TCReverseParserTransactionMap,
  TransactionCompsCell,
  TransactionCompsReverseParser,
  TransactionEnabledKeys,
} from '../types';

const GPT_TRANSACTIONS_FIELDS = [ENTERPRISE_VALUE_ID, LTM_REVENUE_ID, LTM_EBITDA_ID];

const setValue = (
  cell: TransactionCompsCell,
  transactionCompsApproach: ReverseParsedValuationApproachGPT,
  fieldAttributes: GPTApproachAttributes,
  gpcApproaches: Pick<CreateTransactionCompsConfigParams, 'gpcApproaches'>['gpcApproaches']
) => {
  const cellColumnId = cell.columnId as string;
  if (cell.columnId && cell.isGptRow && EDITABLE_COLUMNS.includes(cellColumnId)) {
    const key = transactionCompsApproach.gpt_transactions.findIndex(transaction => {
      const transactionId = transaction.row_ref ?? transaction.id;
      return transactionId === cell.alias;
    });
    const transaction = key !== -1 ? transactionCompsApproach.gpt_transactions[key] : null;
    // Do not overwrite with stale cell values when transaction was enriched from Cap IQ API;
    // cells may not have been re-evaluated yet
    if (key !== -1 && !transaction?.comes_from_capital_iq) {
      transactionCompsApproach.gpt_transactions[key][cellColumnId] = cell.value;
    }
  }
  if (cell.isTitleCell && [PERCENTILE_SELECTION_B_ALIAS, PERCENTILE_SELECTION_A_ALIAS].includes(cell.alias)) {
    transactionCompsApproach[cell.alias as PercentileSelection] = cell.value as number;
  }
  // add condition to check if the cell is from  multiple discount and update the value in updated_gpt_approach
  if (cell.alias === MULTIPLE_DISCOUNT_ALIAS && NTM_MULTIPLES_IDS.includes(cellColumnId)) {
    const metricKey = cellColumnId.split('_')[1]; // this is either revenue or ebitda
    const propertyToUpdate = `ntm_${metricKey}_discount` as DiscountType;
    const decimalPlaces = fieldAttributes[propertyToUpdate]?.decimal_places;
    transactionCompsApproach[propertyToUpdate] = Number(cell.value).toFixed(decimalPlaces as number);
  }

  if (cell.alias === GPC_NTM_EBITDA_APPROACH_ALIAS) {
    updateApproachReference({
      approaches: getArrayValue(gpcApproaches),
      column: transactionCompsApproach,
      lookup: (cell.value ?? '') as string | number,
      specificApproachKey: APPROACH_GPC_PROPERTY,
      updateKey: GPC_NTM_EBITDA_APPROACH_ALIAS,
      referenceKey: REFERENCE_FOR_NTM_EBITDA,
      extraOptions: [{ id: VALUATIONS_SPECIFIED_ID, name: VALUATIONS_SPECIFIED_LABEL }],
    });
  }

  if (cell.alias === GPC_NTM_REVENUE_APPROACH_ALIAS) {
    updateApproachReference({
      approaches: getArrayValue(gpcApproaches),
      column: transactionCompsApproach,
      lookup: (cell.value ?? '') as string | number,
      specificApproachKey: APPROACH_GPC_PROPERTY,
      updateKey: GPC_NTM_REVENUE_APPROACH_ALIAS,
      referenceKey: REFERENCE_FOR_NTM_REVENUE,
      extraOptions: [{ id: VALUATIONS_SPECIFIED_ID, name: VALUATIONS_SPECIFIED_LABEL }],
    });
  }
};

const reverseSelectionCell = (
  cell: TransactionCompsCell,
  transactionCompsApproach: ReverseParsedValuationApproachGPT,
  fieldPrefix = ''
) => {
  const fieldName: ColumnSelection = `${fieldPrefix || (cell.columnId as string)}_${SELECTION}` as ColumnSelection;
  if (cell.value === SPECIFIED_LABEL) {
    transactionCompsApproach[fieldName] = SELECTED_DATABASE_VALUE;
  } else if (VALUES_OPTIONS.includes(cell.value as string)) {
    transactionCompsApproach[fieldName] = cell.value as string;
  } else {
    const percentileOption = (percentileOptions ?? []).find(option => option.label === cell.value);
    if (percentileOption) {
      if (Number(transactionCompsApproach.percentile_selection_a) === percentileOption.value) {
        transactionCompsApproach[fieldName] = SELECTION_A;
      } else if (Number(transactionCompsApproach.percentile_selection_b) === percentileOption.value) {
        transactionCompsApproach[fieldName] = SELECTION_B;
      }
    }
  }
};

function reverseParse(
  cell: TransactionCompsCell,
  transactionMap: TCReverseParserTransactionMap,
  updated_gpt_approach: ReverseParsedValuationApproachGPT,
  fieldAttributes: GPTApproachAttributes,
  gpcApproaches: Pick<CreateTransactionCompsConfigParams, 'gpcApproaches'>['gpcApproaches']
) {
  if (cell.gridType === GRID_NUMBER_CHECKBOX) {
    const ltmCompanyInfoKey = FINANCIALS_COMPANY_INFO_KEYS[cell.columnId as keyof typeof FINANCIALS_COMPANY_INFO_KEYS];
    const transactionItem =
      transactionMap[cell?.alias as string] ?? transactionMap[cell?.row_ref as string] ?? transactionMap[cell?.id as string];

    if (!isEmpty(transactionItem)) {
      transactionItem[`${ltmCompanyInfoKey}_enabled` as TransactionEnabledKeys] = cell?.enabled;
      transactionItem[`${cell.columnId}`] = parseFloat(getStringValue(cell?.revenue)).toFixed(2);
    }
  }

  setValue(cell, updated_gpt_approach, fieldAttributes, gpcApproaches);

  if (MULTIPLE_COLUMNS_LETTER.indexOf(cell.columnLegend as string) >= 0) {
    const financialsCompanyInfoKey =
      FINANCIALS_COMPANY_INFO_KEYS[cell.columnId as keyof typeof FINANCIALS_COMPANY_INFO_KEYS];

    if (cell.alias === SELECTION) {
      // make a variable made up of all characters in the cell.columnId before the underscore
      const prefix = (cell.columnId as string).split('_').slice(0, 2).join('_');
      reverseSelectionCell(cell, updated_gpt_approach, prefix);
    } else if (cell.alias === SELECTED_MULTIPLE) {
      const decimalPlaces =
        fieldAttributes[`${financialsCompanyInfoKey}_${cell.alias}` as keyof GPTApproachAttributes]?.decimal_places;

      const selectedMultipleKey = `${financialsCompanyInfoKey}_${SELECTED_MULTIPLE}` as ColumnSelectedMultipleKey;
      updated_gpt_approach[selectedMultipleKey] = getFixedValue(cell.value, decimalPlaces);
    } else if (cell.alias === WEIGHTING) {
      const weightKey = `${financialsCompanyInfoKey}_${WEIGHTING}` as ColumnWeightKey;
      const decimalPlaces = fieldAttributes[weightKey as keyof GPTApproachAttributes]?.decimal_places;
      updated_gpt_approach[weightKey] = (parseFloat(cell.value as string) / ONE_HUNDRED_VALUE).toFixed(
        Number(decimalPlaces)
      );
    }
  } else if (cell.alias === WEIGHTED_EV) {
    const enterpriseValueDecimals = fieldAttributes.weighted_enterprise_value?.decimal_places;
    updated_gpt_approach.weighted_enterprise_value = getFixedValue(cell.value, enterpriseValueDecimals);
  }
}

const reverseParser: TransactionCompsReverseParser = ({ cells, tableData, fieldAttributes }) => {
  const { gptApproachAttrs, transactionAttrs } = fieldAttributes;
  const transactionCompsAttrs = getObjectValue(gptApproachAttrs);
  if (cells) {
    const { approach, gpcApproaches } = tableData;
    const transactions = approach.valuations_approach_gpt?.gpt_transactions || [];

    const transactionMap = transactions.reduce((map, transaction) => {
      const transactionId: string = transaction?.alias ?? transaction?.row_ref ?? transaction?.id?.toString() ?? '';
      map[transactionId] = {
        ...transaction,
        ...trimUpdateFields({
          comparison: transaction,
          fieldAttributes: transactionAttrs,
          fieldList: GPT_TRANSACTIONS_FIELDS,
        }),
      };
      if (!transaction.comes_from_capital_iq) {
        const updatedTransaction =
          Object.values(cells).find(titleCell => titleCell.row_ref === transaction.row_ref) ??
          ({} as TransactionCompsCell);
        map[transactionId].name = (updatedTransaction.value as string) || transaction.name;
        map[transactionId].target_name = (updatedTransaction.value as string) || transaction.target_name;
      }
      return map;
    }, {} as TCReverseParserTransactionMap);

    const updated_gpt_approach = {
      ...approach.valuations_approach_gpt,
    };

    updated_gpt_approach.gpt_transactions = Object.values(transactionMap).map(transaction => {
      if (transaction.comes_from_capital_iq) {
        return transaction;
      }
      const calculatedEbitdaMargin =
        transaction.ltm_ebitda && transaction.ltm_revenue
          ? (Number(transaction.ltm_ebitda) / Number(transaction.ltm_revenue)).toFixed(
              transactionAttrs?.ltm_ebitda_margin?.decimal_places as number
          )
          : null;
      return {
        ...transaction,
        ltm_ebitda_margin: calculatedEbitdaMargin,
      };
    });
    Object.values(cells).forEach(cell => {
      reverseParse(cell, transactionMap, updated_gpt_approach, transactionCompsAttrs, gpcApproaches);
    });
    tableData.approach.valuations_approach_gpt = {
      ...updated_gpt_approach,
      gpt_transactions: updated_gpt_approach.gpt_transactions.map((transaction, index) => ({
        ...transaction,
        order: index,
      })),
    };
  }
};

export default reverseParser;
